import React, { useState, useMemo, useEffect } from 'react';
import classnames from 'classnames';
import * as Sift from '@sift/types';
import { useSelector } from 'react-redux';
import { isLoading } from 'resift';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';
import _keyBy from 'lodash/keyBy';
import { LayoutUpdateShape } from 'pages/Features/typings';
import * as Types from 'pages/Pages/typings';
import getProfileAttributes from 'pages/Attributes/_helpers/getProfileAttributes';

import {
  HIGHLIGHT_SUBTITLE_COPY,
  highlightByFilter,
} from 'pages/Features/tabs/OrgChart/OrgChartHighlightsLayout';
import {
  FILTER_SUBTITLE_COPY,
  fieldIsFilterable,
} from 'pages/Features/tabs/Directory/FilterLayout';

import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import Typography from '@material-ui/core/Typography';
import Switch from '@material-ui/core/Switch';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContentText from '@material-ui/core/DialogContentText';
import GenericLayoutBuilder from 'components/GenericLayoutBuilder';

import makeStyles from '@material-ui/core/styles/makeStyles';

import { PERSON_ENTITY_TYPE_KEY } from 'helpers/useEntityTypes';
import { Field } from '@sift/types';
import { PERSON_PROFILE_URL } from 'components/Router/consts';

interface Props {
  attribute: Sift.Field;
  entityType: Types.EntityType;
  fields: Sift.Field[];
  status: any;
  typeKey: string;
  values?: LayoutUpdateShape;
  onChange: (field: string, value: any) => void;
}

function AttributeUsage({
  attribute,
  entityType,
  fields,
  status,
  typeKey,
  values,
  onChange,
}: Props) {
  const classes = useStyles();
  const _client = useSelector((state: any) => state.client);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [highlightDialogOpen, setHighlightDialogOpen] = useState(false);

  // Filters-specific items:
  const filterAvailableFields = useMemo(() => {
    const fieldsCollection = (fields || []).filter(fieldIsFilterable);
    return _keyBy(fieldsCollection, 'objectKey');
  }, [fields]);
  const filterLayoutFields = _get(values, 'filters', []);
  const [filterLayout, setFilterLayout] = useState(filterLayoutFields);

  // Highlight-specific items:
  const highlightAvailableFields = useMemo(() => {
    const fieldsCollection = (fields || []).filter((field: Field) =>
      highlightByFilter(field, fields),
    );

    return _keyBy(fieldsCollection, 'objectKey');
  }, [fields]);
  const highlightLayoutFields = _get(values, 'org-chart-highlight-by', []);
  const [highlightLayout, setHighlightLayout] = useState(highlightLayoutFields);

  useEffect(() => {
    setFilterLayout(_get(values, 'filters', []));
    setHighlightLayout(_get(values, 'org-chart-highlight-by', []));
  }, [values]);

  const profileAttributes = useMemo(() => getProfileAttributes(typeKey, entityType, _client), []);
  const isOnProfile = profileAttributes.includes(attribute.objectKey);
  const shouldRenderProfilePage =
    !attribute.objectKey.includes('reachOutToMeFor.') && typeKey === PERSON_ENTITY_TYPE_KEY;

  const filters = _get(entityType, '_layouts.filters', []);
  const nameFieldObjectKey = `${attribute.objectKey}.name`;
  const filterAndHighlightByField =
    fields.find(field => field.objectKey === nameFieldObjectKey) || attribute;
  const isSearchFilterEnabled = filters.some(
    (filter: { name: string; type: string }) => filter.name === filterAndHighlightByField.objectKey,
  );
  const shouldRenderSearchFilters = fieldIsFilterable(filterAndHighlightByField);

  const highlightBy = _get(entityType, '_layouts.org-chart-highlight-by', []);
  const isHighlightByEnabled = highlightBy.some(
    (highlight: { name: string; type: string }) =>
      highlight.name === filterAndHighlightByField.objectKey,
  );
  const shouldRenderHighlightBy = highlightByFilter(filterAndHighlightByField, fields);
  const shouldRenderCard =
    shouldRenderHighlightBy || shouldRenderProfilePage || shouldRenderSearchFilters;

  const closeDialogs = () => {
    setFilterDialogOpen(false);
    setHighlightDialogOpen(false);
  };

  const handleCancel = () => {
    if (!_isEqual(_get(values, 'filters'), filterLayout)) {
      setFilterLayout(filterLayoutFields);
    }
    if (!_isEqual(_get(values, 'org-chart-highlight-by'), highlightLayout)) {
      setHighlightLayout(highlightLayoutFields);
    }
    closeDialogs();
  };

  const handleUpdateLayout = () => {
    if (!_isEqual(_get(values, 'filters', []), filterLayout)) {
      onChange('layouts.filters', filterLayout);
    }
    if (!_isEqual(_get(values, 'org-chart-highlight-by', []), highlightLayout)) {
      onChange('layouts.org-chart-highlight-by', highlightLayout);
    }
    closeDialogs();
  };

  const dialogActions = (
    <DialogActions>
      <Button onClick={handleCancel} disabled={isLoading(status)}>
        Cancel
      </Button>
      <Button
        color="secondary"
        variant="contained"
        onClick={handleUpdateLayout}
        disabled={isLoading(status)}
      >
        Done
      </Button>
    </DialogActions>
  );

  return (
    <>
      {shouldRenderCard && (
        <Card className={classes.cardRoot}>
          <CardHeader
            title={`${typeKey === PERSON_ENTITY_TYPE_KEY ? 'Person ' : ''}Attribute Usage`}
            subheader="The places in Sift where the attribute is visible."
          />
          <Divider />
          <CardContent>
            {shouldRenderProfilePage && (
              <div className={classes.content}>
                <div>
                  <Typography variant="h6">Profile Page</Typography>
                  <Typography variant="subtitle2">
                    To add the attribute to the Profile Page and set its position, go to{' '}
                    <a
                      className={classes.link}
                      href={`/admin${PERSON_PROFILE_URL}/layout`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Profile Layout
                    </a>{' '}
                    after saving changes in this page.
                  </Typography>
                </div>
                <Switch
                  id="profile-page-switch"
                  inputProps={{ 'aria-label': 'profile page switch' }}
                  checked={isOnProfile}
                  disabled
                />
              </div>
            )}
            {shouldRenderSearchFilters && (
              <div
                className={classnames(classes.content, {
                  [classes.marginTop]: shouldRenderProfilePage,
                })}
              >
                {' '}
                <div>
                  <Typography variant="h6">Search Filters</Typography>
                  <div>
                    <Typography variant="subtitle2">
                      If enabled, people can filter by this attribute on the Directory and Explore
                      Pages. To add to your filter list,
                      <Button
                        className={classes.positionButton}
                        onClick={() => {
                          setFilterDialogOpen(true);
                        }}
                      >
                        click here.
                      </Button>
                    </Typography>
                  </div>
                </div>
                <Switch
                  id="search-filter-switch"
                  inputProps={{ 'aria-label': 'search filter switch' }}
                  checked={isSearchFilterEnabled}
                  disabled
                />
              </div>
            )}
            {shouldRenderHighlightBy && (
              <div
                className={classnames(classes.content, {
                  [classes.marginTop]: shouldRenderSearchFilters || shouldRenderProfilePage,
                })}
              >
                <div>
                  <Typography variant="h6">Org Chart Highlight By</Typography>
                  <Typography variant="subtitle2">
                    If enabled, people can color code by this attribute on the Org Chart. To add to
                    your list of highlightable attributes,
                    <Button
                      className={classes.positionButton}
                      onClick={() => {
                        setHighlightDialogOpen(true);
                      }}
                    >
                      click here.
                    </Button>
                  </Typography>
                </div>
                <Switch
                  id="highlight-switch"
                  inputProps={{ 'aria-label': 'highlight switch' }}
                  checked={isHighlightByEnabled}
                  disabled
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <Dialog
        open={filterDialogOpen}
        onClose={() => setFilterDialogOpen(false)}
        classes={{ paper: classes.rootDialog }}
      >
        <DialogTitle>Configure Filters</DialogTitle>
        <DialogContent>
          <DialogContentText>{FILTER_SUBTITLE_COPY}</DialogContentText>
          <GenericLayoutBuilder
            allFields={fields}
            fields={filterAvailableFields}
            layout={filterLayout}
            typeKey={typeKey}
            onChange={setFilterLayout}
          />
        </DialogContent>
        {dialogActions}
      </Dialog>
      <Dialog
        open={highlightDialogOpen}
        onClose={() => setHighlightDialogOpen(false)}
        classes={{ paper: classes.rootDialog }}
      >
        <DialogTitle>Configure Highlighting</DialogTitle>
        <DialogContent>
          <DialogContentText>{HIGHLIGHT_SUBTITLE_COPY}</DialogContentText>
          <GenericLayoutBuilder
            allFields={fields}
            layout={highlightLayout}
            onChange={setHighlightLayout}
            fields={highlightAvailableFields}
            typeKey={typeKey}
          />
        </DialogContent>
        {dialogActions}
      </Dialog>
    </>
  );
}

const useStyles = makeStyles(theme => ({
  root: {},
  cardRoot: {
    maxWidth: 900,
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  rootDialog: {
    minHeight: 700,
    minWidth: 500,
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  positionButton: {
    textTransform: 'none',
    color: theme.palette.secondary.main,
    marginBottom: theme.spacing(0.5),
  },
  link: {
    textDecoration: 'underline',
  },
}));

export default AttributeUsage;
